import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

function AccountSettings() {
    const { t } = useTranslation();
    const { currentUser, userRole, updateUserPassword, logout, deleteAccount } = useAuth();
    const navigate = useNavigate();

    // Password change states
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [passwordMessage, setPasswordMessage] = useState('');
    const [passwordLoading, setPasswordLoading] = useState(false);

    // Show/hide password change form
    const [showPasswordForm, setShowPasswordForm] = useState(false);

    // Delete account states
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletePassword, setDeletePassword] = useState('');
    const [deleteError, setDeleteError] = useState('');
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Redirect if not logged in
    if (!currentUser) {
        navigate('/login');
        return null;
    }

    async function handlePasswordChange(e) {
        e.preventDefault();

        if (newPassword !== confirmNewPassword) {
            return setPasswordError(t('auth_passwords_mismatch'));
        }

        if (newPassword.length < 6) {
            return setPasswordError(t('auth_password_too_short'));
        }

        try {
            setPasswordError('');
            setPasswordMessage('');
            setPasswordLoading(true);
            await updateUserPassword(currentPassword, newPassword);
            setPasswordMessage(t('auth_password_updated'));
            setCurrentPassword('');
            setNewPassword('');
            setConfirmNewPassword('');
            setShowPasswordForm(false);
        } catch (err) {
            if (err.code === 'auth/wrong-password') {
                setPasswordError(t('auth_wrong_current_password'));
            } else {
                setPasswordError(t('auth_password_update_failed') + err.message);
            }
        }
        setPasswordLoading(false);
    }

    async function handleLogout() {
        try {
            await logout();
            navigate('/');
        } catch (error) {
            console.error('Failed to logout:', error);
        }
    }

    async function handleDeleteAccount(e) {
        e.preventDefault();

        if (!deletePassword) {
            return setDeleteError(t('account_delete_password_required'));
        }

        try {
            setDeleteError('');
            setDeleteLoading(true);
            await deleteAccount(deletePassword);
            navigate('/');
        } catch (err) {
            if (err.code === 'auth/wrong-password') {
                setDeleteError(t('auth_wrong_current_password'));
            } else {
                setDeleteError(t('account_delete_failed') + err.message);
            }
            setDeleteLoading(false);
        }
    }

    return (
        <div className="page-section">
            <div className="account-settings-container">
                <h2 className="section-title">{t('account_settings_title')}</h2>
                <p className="section-subtitle">{t('account_settings_subtitle')}</p>

                {/* Account Info Card */}
                <div className="account-card">
                    <div className="account-card-header">
                        <span className="account-icon">👤</span>
                        <h3>{t('account_info_title')}</h3>
                    </div>
                    <div className="account-card-body">
                        <div className="account-info-row">
                            <span className="info-label">{t('auth_email')}:</span>
                            <span className="info-value">{currentUser.email}</span>
                        </div>
                        <div className="account-info-row">
                            <span className="info-label">{t('account_role')}:</span>
                            <span className={`info-value role-badge ${userRole === 'admin' ? 'admin' : 'user'}`}>
                                {userRole === 'admin' ? '👑 ' : ''}{t(`account_role_${userRole}`)}
                            </span>
                        </div>
                        <div className="account-info-row">
                            <span className="info-label">{t('account_member_since')}:</span>
                            <span className="info-value">
                                {currentUser.metadata?.creationTime
                                    ? new Date(currentUser.metadata.creationTime).toLocaleDateString()
                                    : t('account_unknown')}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Security Card */}
                <div className="account-card">
                    <div className="account-card-header">
                        <span className="account-icon">🔒</span>
                        <h3>{t('account_security_title')}</h3>
                    </div>
                    <div className="account-card-body">
                        {passwordMessage && <div className="success-banner">{passwordMessage}</div>}
                        {passwordError && <div className="error-banner">{passwordError}</div>}

                        {!showPasswordForm ? (
                            <button
                                className="btn-secondary-modern"
                                onClick={() => setShowPasswordForm(true)}
                            >
                                {t('account_change_password_btn')}
                            </button>
                        ) : (
                            <form onSubmit={handlePasswordChange} className="password-change-form">
                                <div className="form-group">
                                    <label htmlFor="currentPassword">{t('auth_current_password')}</label>
                                    <input
                                        type="password"
                                        id="currentPassword"
                                        className="form-input"
                                        value={currentPassword}
                                        onChange={(e) => setCurrentPassword(e.target.value)}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="newPassword">{t('auth_new_password')}</label>
                                    <input
                                        type="password"
                                        id="newPassword"
                                        className="form-input"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        required
                                        minLength="6"
                                    />
                                    <small className="form-hint">{t('auth_password_hint')}</small>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="confirmNewPassword">{t('auth_confirm_new_password')}</label>
                                    <input
                                        type="password"
                                        id="confirmNewPassword"
                                        className="form-input"
                                        value={confirmNewPassword}
                                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                                        required
                                    />
                                </div>

                                <div className="form-actions">
                                    <button
                                        type="button"
                                        className="btn-outline-modern"
                                        onClick={() => {
                                            setShowPasswordForm(false);
                                            setPasswordError('');
                                            setCurrentPassword('');
                                            setNewPassword('');
                                            setConfirmNewPassword('');
                                        }}
                                    >
                                        {t('cancel')}
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn-primary-modern"
                                        disabled={passwordLoading}
                                    >
                                        {passwordLoading ? t('auth_updating') : t('auth_update_password_btn')}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>

                {/* Actions Card */}
                <div className="account-card">
                    <div className="account-card-header">
                        <span className="account-icon">⚙️</span>
                        <h3>{t('account_actions_title')}</h3>
                    </div>
                    <div className="account-card-body">
                        <button
                            className="btn-danger-outline"
                            onClick={handleLogout}
                        >
                            {t('nav_logout')}
                        </button>
                    </div>
                </div>

                {/* Danger Zone - Delete Account */}
                <div className="account-card danger-zone">
                    <div className="account-card-header">
                        <span className="account-icon">⚠️</span>
                        <h3>{t('account_delete_title')}</h3>
                    </div>
                    <div className="account-card-body">
                        <p className="danger-warning">{t('account_delete_confirm_message')}</p>
                        <button
                            className="btn-danger-modern"
                            onClick={() => setShowDeleteModal(true)}
                        >
                            {t('account_delete_btn')}
                        </button>
                    </div>
                </div>
            </div>

            {/* Delete Account Modal */}
            {showDeleteModal && (
                <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-content modal-confirm" onClick={(e) => e.stopPropagation()}>
                        <h3 className="modal-title">⚠️ {t('account_delete_confirm_title')}</h3>
                        <p className="modal-message">
                            {t('account_delete_confirm_message')}
                        </p>

                        {deleteError && <div className="error-banner">{deleteError}</div>}

                        <form onSubmit={handleDeleteAccount}>
                            <div className="form-group">
                                <label htmlFor="deletePassword">{t('auth_current_password')}</label>
                                <input
                                    type="password"
                                    id="deletePassword"
                                    className="form-input"
                                    value={deletePassword}
                                    onChange={(e) => setDeletePassword(e.target.value)}
                                    placeholder={t('account_delete_password_required')}
                                    required
                                    autoFocus
                                />
                            </div>
                            <div className="modal-actions">
                                <button
                                    type="button"
                                    className="btn-outline-modern"
                                    onClick={() => {
                                        setShowDeleteModal(false);
                                        setDeletePassword('');
                                        setDeleteError('');
                                    }}
                                >
                                    {t('cancel')}
                                </button>
                                <button
                                    type="submit"
                                    className="btn-danger-modern"
                                    disabled={deleteLoading}
                                >
                                    {deleteLoading ? t('account_deleting') : t('account_delete_confirm_btn')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AccountSettings;
